import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Clock, Package, Zap, AlertCircle } from 'lucide-react';
import Header from '../components/Header';

const TurnaroundTime = ({ cartItems, onRemoveFromCart, onUpdateQuantity }) => {
  return (
    <div className="min-h-screen bg-warm-ivory">
      <Header
        cartItems={cartItems}
        onRemoveFromCart={onRemoveFromCart}
        onUpdateQuantity={onUpdateQuantity}
      />

      {/* Hero Section */}
      <section className="pt-40 md:pt-44 pb-12 px-6">
        <div className="container mx-auto max-w-4xl text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-charcoal-ink mb-6">
            Turnaround Time Policy
          </h1>
          <p className="text-xl text-stone-taupe">
            Plan ahead to ensure your designs arrive on time for your special day
          </p>
        </div>
      </section>

      {/* Turnaround Time Content */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-4xl space-y-8">
          
          {/* Digital Designs */}
          <div className="bg-white rounded-lg p-8 border border-stone-taupe/20">
            <div className="flex items-start gap-4 mb-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-charcoal-ink flex items-center justify-center">
                <Clock className="w-6 h-6 text-warm-ivory" />
              </div>
              <div className="flex-grow">
                <h2 className="text-2xl font-bold text-charcoal-ink mb-3">Digital Designs</h2>
                <p className="text-charcoal-ink/80 leading-relaxed">
                  Digital orders are typically delivered within <strong>3–5 business days</strong> after all required information is received.
                </p>
              </div>
            </div>
          </div>

          {/* Printed Items */}
          <div className="bg-white rounded-lg p-8 border border-stone-taupe/20">
            <div className="flex items-start gap-4 mb-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-charcoal-ink flex items-center justify-center">
                <Package className="w-6 h-6 text-warm-ivory" />
              </div>
              <div className="flex-grow">
                <h2 className="text-2xl font-bold text-charcoal-ink mb-3">Printed Items</h2>
                <p className="text-charcoal-ink/80 leading-relaxed">
                  Printed orders require additional production and shipping time. We recommend placing printed orders at least <strong>3–4 weeks before your event date</strong>.
                </p>
              </div>
            </div>
          </div>

          {/* Rush Orders */}
          <div className="bg-white rounded-lg p-8 border border-stone-taupe/20">
            <div className="flex items-start gap-4 mb-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-charcoal-ink flex items-center justify-center">
                <Zap className="w-6 h-6 text-warm-ivory" />
              </div>
              <div className="flex-grow">
                <h2 className="text-2xl font-bold text-charcoal-ink mb-3">Rush Orders</h2>
                <p className="text-charcoal-ink/80 leading-relaxed">
                  Rush service may be available for select digital orders for an additional fee and is subject to availability.
                </p>
              </div>
            </div>
          </div>

          {/* Important Notice */}
          <div className="bg-soft-linen rounded-lg p-8 border border-stone-taupe/20">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-charcoal-ink flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-warm-ivory" />
              </div>
              <div className="flex-grow">
                <h3 className="text-xl font-semibold text-charcoal-ink mb-2">Important Notice</h3>
                <p className="text-charcoal-ink/80 leading-relaxed">
                  Please note that turnaround times may vary during peak seasons and holidays.
                </p>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-soft-linen">
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-charcoal-ink mb-4">
            Ready to get started?
          </h2>
          <p className="text-xl text-stone-taupe mb-8">
            Browse our bundles or submit a custom request to begin your design journey.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/">
              <Button
                size="lg"
                className="bg-charcoal-ink hover:bg-charcoal-ink/90 text-warm-ivory text-lg px-8 py-6"
              >
                Shop Bundles
              </Button>
            </Link>
            <Link to="/?action=custom">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-charcoal-ink text-charcoal-ink hover:bg-soft-linen text-lg px-8 py-6"
              >
                Submit Custom Request
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-charcoal-ink text-warm-ivory py-12 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_partyfavor-design/artifacts/kg186rqs_Photoroom_20260106_152029.png" 
                alt="The Favor" 
                className="h-20 md:h-24 w-auto mb-4 brightness-0 invert"
              />
              <p className="text-warm-ivory/80">
                Custom party details, designed to be remembered.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Shop</h4>
              <ul className="space-y-2">
                <li><Link to="/#bundles" className="text-warm-ivory/80 hover:text-warm-ivory transition-colors">Party Bundles</Link></li>
                <li><Link to="/#occasions" className="text-warm-ivory/80 hover:text-warm-ivory transition-colors">Occasions</Link></li>
                <li><Link to="/?action=custom" className="text-warm-ivory/80 hover:text-warm-ivory transition-colors">Custom Requests</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Information</h4>
              <ul className="space-y-2">
                <li><Link to="/about" className="text-warm-ivory/80 hover:text-warm-ivory transition-colors">About</Link></li>
                <li><Link to="/faq" className="text-warm-ivory/80 hover:text-warm-ivory transition-colors">FAQ</Link></li>
                <li><Link to="/turnaround-time" className="text-warm-ivory/80 hover:text-warm-ivory transition-colors">Turnaround Times</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Connect</h4>
              <ul className="space-y-2">
                <li><a href="https://www.instagram.com/thefavor.design/" target="_blank" rel="noopener noreferrer" data-testid="footer-instagram-link" className="text-warm-ivory/80 hover:text-warm-ivory transition-colors">Instagram</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-warm-ivory/20 pt-8 text-center text-warm-ivory/60">
            <p>&copy; 2025 The Favor. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TurnaroundTime;